import React, { Component, ErrorInfo, PropsWithChildren } from 'react';
import { ErrorDisplay } from './ErrorDisplay';
import { getErrorDetails, ErrorCategory } from '../../../utils/errorMapping';
import { logger } from '../../../utils/logging';
import { logError } from '../../../utils/monitoring/faro';
import { FetchError } from '../../../utils/api';

interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Get categorized error details
    const errorDetails = getErrorDetails(error);
    
    // Add metadata to the error object for better tracking
    const componentStack = errorInfo.componentStack || '';
    
    // Extract API error details if available
    const apiErrorDetails = (error as FetchError).isApiError ? {
      url: (error as FetchError).url,
      status: (error as FetchError).status,
      statusText: (error as FetchError).statusText,
      apiStatusCode: (error as FetchError).apiStatusCode,
      apiMessage: (error as FetchError).apiMessage,
      responseBody: (error as FetchError).responseBody?.substring(0, 500),
      hasOriginalError: !!(error as FetchError).originalError
    } : {};
    
    const metadata = {
      category: errorDetails.category,
      // Only include first 3 lines of component stack in production
      componentStack: import.meta.env.PROD 
        ? componentStack.split('\n').slice(0, 3).join('\n')
        : componentStack,
      // Add useful debugging information
      url: window.location.href,
      timestamp: new Date().toISOString(),
      // Include API error details if available
      ...apiErrorDetails
    };

    // Log to Faro with original message and metadata
    logError(error, errorDetails.messageKey, {
      ...metadata,
      originalMessage: error.message,
      source: 'react',
      type: 'boundary'
    });

    // Log to local logger for development
    if (process.env.NODE_ENV === 'development') {
      logger.error('React Error Boundary Caught Error', error, {
        component: 'ErrorBoundary',
        category: errorDetails.category,
        // Include basic error location without full stack
        location: componentStack.split('\n')[1] || 'unknown',
        // Include API error details if available
        ...apiErrorDetails
      });
      
      // Log additional details for API errors
      if ((error as FetchError).isApiError) {
        console.error('API Error Details:', {
          url: (error as FetchError).url,
          status: (error as FetchError).status,
          statusText: (error as FetchError).statusText,
          apiStatusCode: (error as FetchError).apiStatusCode,
          apiMessage: (error as FetchError).apiMessage,
          responseBody: (error as FetchError).responseBody,
          originalError: (error as FetchError).originalError
        });
      }
    }

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    });
    
    // Only reload for severe errors
    const errorDetails = getErrorDetails(this.state.error);
    if (
      errorDetails.category === ErrorCategory.SERVER_ERROR ||
      errorDetails.category === ErrorCategory.UNKNOWN
    ) {
      window.location.reload();
    }
  };

  render() {
    const { children, fallback } = this.props;
    const { hasError, error } = this.state;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <ErrorDisplay
          error={error}
          onRetry={this.handleReset}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary; 